import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Copy } from "@phosphor-icons/react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { useVerifiedFactor } from "../auth/stepUp";
import {
  isFinalSecretFailure,
  secretFailureText,
  useRevealSecret,
  useSecretTransfers
} from "../data/secretQueries";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { ClientHead } from "../ui/ClientHead";
import "./clientSettings/settings.css";

/**
 * The recipient's one view, plan.md section 5.10.
 *
 * `public.consume_one_time_secret` is one conditional statement. It returns the ciphertext and
 * clears it in the same `update`, so a second attempt wins nothing and is told
 * `PORTAL_SECRET_NOT_VIEWABLE`. That is the whole guarantee, and everything on this screen exists so
 * that the one view a person gets is a view they were ready for.
 *
 * ---
 *
 * **Nothing here consumes on arrival.** Opening this address, refreshing it, or reaching it from the
 * back button does nothing at all: the screen renders what the transfer is about and asks. A
 * component that consumed in an effect would burn a secret for somebody who clicked a link in a
 * notification to see what it was, and `React.StrictMode` would burn it twice in development. So the
 * act is a form submission, and it is the second of two deliberate acts rather than the first.
 *
 * **A double click cannot spend it twice.** `attempted` is a ref set synchronously before the await,
 * so the second click of a double click returns before it reaches the network. The button is also
 * disabled while the mutation is pending, which is the part a person can see; the ref is the part
 * that holds when two events land in the same tick.
 *
 * **A failure after consumption is final and the screen says so rather than offering a retry.**
 * `worker/src/routes/secrets.ts` records the refusal through `public.record_secret_view_refusal` and
 * answers `secret_unrecoverable`, and architecture.md section 10 fixes what that means: "Failure
 * after database consumption never makes the value viewable a second time; the UI reports that
 * support is required. This conservative failure mode preserves 'one view'." A retry button under
 * that sentence would be the portal promising something the design has ruled out, so
 * `isFinalSecretFailure` decides whether one is drawn at all.
 *
 * **The value is component state and nothing else.** It is never a query, never mutation data and
 * never a mutation variable; `app/data/secretQueries.ts` carries that argument in full. It is
 * cleared when the id in the address changes, which is the one case where this component stays
 * mounted and would otherwise hold the previous secret while showing the next one's heading.
 *
 * **The rotation advice is here rather than on the sender's screen**, because plan.md 5.10 puts it
 * on the interface that shows the value: "The interface tells the recipient to rotate a shared
 * password or key when practical."
 *
 * **One narrow box with a way back, since the second build round of 2026-09-24**
 * (`prototype/kund/pages.js`, `secretPage`). The three steps and every refusal are drawn in the same
 * box under "Öppna en hemlighet". The step before the code kept its one sentence about what cannot be
 * undone and lost the advice to wait, and the code step's reason became the field's own line.
 */

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type Stage = "intro" | "confirm" | "revealed" | "failed";

export function SecretViewRoute() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const identity = usePortalIdentity();
  const factor = useVerifiedFactor();
  const tenantId = identity.data?.tenantId ?? null;

  const transfers = useSecretTransfers(Boolean(tenantId));

  const [stage, setStage] = useState<Stage>("intro");
  const [code, setCode] = useState("");
  const [value, setValue] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [finalFailure, setFinalFailure] = useState(false);
  const [copied, setCopied] = useState(false);

  /**
   * The transfer this browser tab has already spent its one attempt on, or null.
   *
   * A ref rather than state, because the guard has to hold **inside one tick**: two clicks that land
   * before React has re-rendered would both read the same stale state value and both reach the
   * network, which is precisely the double click this exists to stop.
   *
   * It holds the id rather than a boolean, so that an address change needs no reset at all. That is
   * not tidiness: `react-hooks/refs` refuses a ref written during render, and the reset below runs
   * during render.
   */
  const attemptedFor = useRef<string | null>(null);
  const revealedRegion = useRef<HTMLDivElement>(null);

  const reveal = useRevealSecret(setValue);

  /**
   * Everything resets when the address names a different transfer.
   *
   * React Router reuses this component across a parameter change, so without this the previous
   * secret would still be in state while the heading described the next one, and `attempted` would
   * still be true so the new one could never be opened at all. Unmounting drops the state on its
   * own; this is the case unmounting does not cover.
   *
   * **Adjusted during render rather than in an effect, and the difference matters here.** An effect
   * runs after the browser has already painted, so for one frame the previous transfer's decrypted
   * value would be on screen under the new transfer's heading. Resetting during render means React
   * discards the output it was about to commit and re-runs this component immediately, before
   * anything is shown. It is the pattern React documents for exactly this case, and
   * `react-hooks/set-state-in-effect` refuses the effect version.
   *
   * `attemptedFor` is deliberately not reset here. It holds the id it was spent on, so an address
   * change makes it stale on its own and nothing has to write a ref during render.
   */
  const [renderedId, setRenderedId] = useState(id);

  if (renderedId !== id) {
    setRenderedId(id);
    setStage("intro");
    setCode("");
    setValue(null);
    setPurpose(null);
    setFailure(null);
    setFinalFailure(false);
    setCopied(false);
  }

  // Moving focus into the value is what tells a screen reader something arrived, without an
  // `aria-live` region that would announce a password the moment it appeared.
  useEffect(() => {
    if (stage === "revealed") {
      revealedRegion.current?.focus();
    }
  }, [stage]);

  /*
    One narrow box under a head with the way back, whatever the step (the sketch's `secretPage`).
    Every refusal is drawn inside the same box, so the page never changes shape between the states.
  */
  const frame = (children: ReactNode) => (
    <div className="k-page k-secrets">
      <ClientHead title="Öppna en hemlighet" back={{ to: "/secrets", label: "Hemligheter" }} />
      <section className="panel k-secret-box">
        {children}
      </section>
    </div>
  );

  if (!UUID_PATTERN.test(id)) {
    return frame(
      <BoxState
        kind="denied"
        title="Adressen stämmer inte"
        body="Länken pekar inte på någon hemlighet. Öppna notisen i portalen i stället, så leder den rätt."
      />
    );
  }

  if (identity.isLoading || transfers.isLoading) {
    return frame(<BoxState kind="loading" title="Hämtar" />);
  }

  if (identity.isError || !tenantId) {
    return frame(
      <BoxState
        kind="denied"
        title="Ingen aktiv åtkomst"
        body="Vi kan inte se vilket kundkonto du hör till, så ingenting kan öppnas härifrån. Ladda om sidan, och skriv i chatten om det står kvar."
      />
    );
  }

  if (transfers.isError) {
    return frame(
      <BoxState
        kind="error"
        title="Vi kunde inte läsa av hemligheten"
        body="Ingenting har öppnats och ingenting har ändrats. Försök igen."
        onRetry={() => void transfers.refetch()}
      />
    );
  }

  const transfer = (transfers.data ?? []).find((row) => row.id === id);

  /*
   * The row is gone or is not the caller's, and those are one answer on purpose.
   *
   * `public.consume_one_time_secret` gives the same refusal to a stranger, to the sender and to a
   * transfer that never existed, so that nobody learns whether it exists. The screen keeps that
   * rather than guessing, and it is also the honest answer for the ordinary case: a viewed transfer
   * is deleted a day later by `secret_view_or_7d` and an unopened one at its expiry, so a row that
   * has simply aged out of the table lands here too.
   */
  if (!transfer || transfer.direction !== "received") {
    return frame(
      <BoxState
        kind="denied"
        title="Hemligheten finns inte här"
        body="Den är öppnad, återkallad eller utgången, eller så är den inte skickad till er. Be avsändaren skicka en ny."
      />
    );
  }

  if (transfer.status !== "waiting" && stage !== "revealed") {
    return frame(
      <BoxState
        kind="denied"
        title="Hemligheten går inte att öppna"
        body="Den är redan öppnad, återkallad eller utgången, och den går inte att visa igen. Be avsändaren skicka en ny."
      />
    );
  }

  if (factor.isError) {
    return frame(
      <div className="k-secret-steps">
        <p className="form-error" role="alert" data-testid="secret-view-factor-unknown">
          Vi kunde inte läsa om ditt konto har en verifieringsapp. Ingenting har öppnats. Ladda om sidan
          och försök igen.
        </p>
      </div>
    );
  }

  if (factor.data === undefined) {
    return frame(<BoxState kind="loading" title="Läser av ditt konto" />);
  }

  /*
   * Unreachable in practice and answered anyway.
   *
   * `public.create_one_time_secret` refuses a recipient without a verified factor, so an account
   * that reaches this screen had one when the transfer was made. It could have removed it since, and
   * the step-up would then be refused with `PORTAL_STEP_UP_AAL2_REQUIRED` after a code field had
   * been offered for a code the account cannot produce. Saying it first is cheaper. The way out goes
   * to the section that holds the factor, since Inställningar became sections on 2026-09-24.
   */
  if (factor.data === null) {
    return frame(
      <div className="k-secret-steps" data-testid="secret-view-needs-mfa">
        <h2 className="k-secret-purpose">Ditt konto saknar verifieringsapp</h2>
        <p className="quiet">
          En hemlighet får bara öppnas av ett konto som är skyddat med tvåfaktorsinloggning. Slå på
          tvåfaktor under Inställningar, så går den att öppna här.
        </p>
        <Link className="button is-primary is-block" to="/settings/security">
          Gå till Inställningar
        </Link>
      </div>
    );
  }

  const factorId = factor.data;

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Synchronous, and before anything awaits. Two clicks in the same tick both reach this line and
    // only the first one gets past it.
    if (attemptedFor.current === id) {
      return;
    }
    attemptedFor.current = id;

    setFailure(null);

    try {
      const opened = await reveal.mutateAsync({
        id,
        tenantId: tenantId as string,
        factorId,
        code
      });

      setPurpose(opened.purposeLabel);
      setStage("revealed");
    } catch (error) {
      setFailure(secretFailureText(error));
      setFinalFailure(isFinalSecretFailure(error));
      setStage("failed");
    } finally {
      // The code is spent whatever happened. A TOTP code is single use, so leaving it in the field
      // would leave a value that cannot work looking like one that can.
      setCode("");
    }
  }

  if (stage === "revealed" && value !== null) {
    return frame(
      <div
        className="k-secret-steps secret-revealed"
        ref={revealedRegion}
        tabIndex={-1}
        data-testid="secret-revealed"
      >
        <h2 className="k-secret-purpose">{purpose ?? transfer.purpose_label}</h2>
        <Callout tone="warn">
          Det här är enda gången den visas. Den är borta ur portalen nu, även för avsändaren.
        </Callout>

        <pre className="k-secret-value" data-testid="secret-value-output">
          <code>{value}</code>
        </pre>

        <div className="form-actions">
          <button
            type="button"
            className="button"
            data-testid="secret-copy"
            onClick={() => {
              void navigator.clipboard?.writeText(value).then(
                () => setCopied(true),
                () => setCopied(false)
              );
            }}
          >
            <Copy size={16} aria-hidden="true" />
            {copied ? "Kopierad" : "Kopiera"}
          </button>

          <button
            type="button"
            className="button is-primary"
            data-testid="secret-done"
            onClick={() => {
              setValue(null);
              void navigate("/secrets");
            }}
          >
            Jag är klar
          </button>
        </div>

        {/*
          plan.md 5.10, last bullet: "The interface tells the recipient to rotate a shared password
          or key when practical." It is placed under the value rather than over it, because somebody
          who has one view is reading the value first whatever else is on the page.
        */}
        <p className="line-hint" data-testid="secret-rotate-advice">
          Är det ett lösenord eller en nyckel som flera nu känner till, byt den när det går. En
          hemlighet som har delats är delad, hur säkert den än kom fram.
        </p>
      </div>
    );
  }

  if (stage === "failed") {
    return frame(
      <div className="k-secret-steps" data-testid="secret-view-failed">
        <h2 className="k-secret-purpose">Hemligheten visades inte</h2>

        <p className="form-error" role="alert" data-testid="secret-view-error">
          {failure}
        </p>

        {/*
          A retry only where one can work. `isFinalSecretFailure` is true for the two refusals that
          are permanent by design: the transfer was not viewable, and the decryption failed after the
          conditional statement had already consumed it. Offering a button under either would teach a
          client that the portal is unreliable rather than that the value is gone.
        */}
        {finalFailure ? (
          <Link className="button is-block" to="/secrets">
            Tillbaka
          </Link>
        ) : (
          <button
            type="button"
            className="button is-block"
            data-testid="secret-try-again"
            onClick={() => {
              // An event handler, so writing the ref here is exactly where a ref may be written.
              // The attempt is released only on the refusals that are not final: the branch above
              // draws a way back instead of this button for the two that are.
              attemptedFor.current = null;
              setFailure(null);
              setStage("confirm");
            }}
          >
            Försök igen
          </button>
        )}
      </div>
    );
  }

  if (stage === "intro") {
    return frame(
      <div className="k-secret-steps" data-testid="secret-intro">
        <h2 className="k-secret-purpose">{transfer.purpose_label}</h2>
        {/*
          The one sentence on the confirmation of something that cannot be undone, which is the one
          kind of paragraph design.md section 3's third rule keeps.
        */}
        <p className="quiet">
          Den här är skickad till er i portalen. Den går att öppna en enda gång. När den är öppnad finns
          den inte kvar, varken hos er eller hos avsändaren, och den går inte att visa igen.
        </p>

        <button
          type="button"
          className="button is-primary is-block"
          data-testid="secret-continue"
          onClick={() => setStage("confirm")}
        >
          Jag är redo
        </button>
      </div>
    );
  }

  return frame(
    <form className="k-secret-steps" onSubmit={submit} data-testid="secret-confirm">
      <h2 className="k-secret-purpose">{transfer.purpose_label}</h2>

      <div className="field">
        <label htmlFor="secret-view-code">Kod från din verifieringsapp</label>
        <input
          id="secret-view-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          disabled={reveal.isPending}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
        />
        <p className="settings-note">
          Vi behöver veta att det är du innan värdet visas. Koden från din verifieringsapp är det som
          styrker det, och den gäller i fem minuter.
        </p>
      </div>

      <button
        type="submit"
        className="button is-primary is-block"
        disabled={code.length < 6 || reveal.isPending}
        data-testid="secret-reveal"
      >
        {reveal.isPending ? "Öppnar…" : "Visa hemligheten"}
      </button>
    </form>
  );
}
